import { DeviceMobileIcon } from '@phosphor-icons/react';
import { Icon } from '@sr/ui-library';
import { Spinner } from '../../../Loader';
import { ErrorBanner } from './SetupContainer';

type MfaMethod = 'sms' | 'totp';

interface MethodChooserProps {
  chosenMethod: MfaMethod | null;
  error: string;
  isLoading: boolean;
  onBack: () => void;
  onChoose: (method: MfaMethod) => void;
}

function MethodChooser({
  chosenMethod,
  error,
  isLoading,
  onBack,
  onChoose,
}: MethodChooserProps) {
  return (
    <div className="p-6">
      <div className="text-center">
        <h4 className="text-lg font-bold">Choose your method</h4>
        <p className="pb-4 pt-2 text-sm text-sr-text-secondary">
          How would you like to receive your verification codes?
        </p>
      </div>

      <div className="mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 px-4">
        <Icon className="mt-0.5 flex-shrink-0 text-blue-500" icon="info" />
        <p className="text-sm">
          We recommend use of an authenticator app on your phone such as{' '}
          <strong>Google Authenticator</strong>,{' '}
          <strong>Microsoft Authenticator</strong> or <strong>Authy</strong>.
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      <MethodCard
        title="Authenticator App"
        badge="Recommended"
        description="Use an app like Google Authenticator, Microsoft Authenticator, or Authy"
        icon={
          <Icon
            className="text-sr-text-brand"
            icon={DeviceMobileIcon}
            size="xl"
          />
        }
        isLoading={isLoading && chosenMethod === 'totp'}
        onClick={() => onChoose('totp')}
      />

      <div className="pt-3">
        <MethodCard
          title="Text Message (SMS)"
          description="Receive a code via text message to your phone number"
          icon={
            <Icon className="text-sr-text-brand" icon="message" size="xl" />
          }
          isLoading={isLoading && chosenMethod === 'sms'}
          onClick={() => onChoose('sms')}
        />
      </div>

      <button
        className="mt-6 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
        type="button"
        onClick={onBack}
      >
        Back
      </button>
    </div>
  );
}

function MethodCard({
  badge,
  description,
  icon,
  isLoading,
  onClick,
  title,
}: {
  badge?: string;
  description: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      className="group flex w-full items-start gap-3 rounded-lg border border-read-only-bg bg-white p-4 text-left transition-all hover:border-sr-text-brand hover:ring-1 hover:ring-sr-text-brand disabled:cursor-wait disabled:opacity-70"
      disabled={isLoading}
      type="button"
      onClick={onClick}
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sr-light-red">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-gray-900">{title}</span>
          {badge && (
            <span className="rounded bg-sr-green-secondary px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-sr-text-success">
              {badge}
            </span>
          )}
        </div>
        <span className="mt-0.5 block text-sm text-sr-light-gray">
          {description}
        </span>
      </div>
      <div className="flex flex-shrink-0 items-center pt-1 text-gray-400">
        {isLoading ? (
          <Spinner size="4" />
        ) : (
          <Icon icon="chevronRight" size="sm" />
        )}
      </div>
    </button>
  );
}

export type { MfaMethod };
export default MethodChooser;
